import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import CustomButton from "../customButton/customButton";
import { getCroppedImgFile } from "../../utils/imageCropUtils";

import "react-easy-crop/react-easy-crop.css";
import "./imageCropModal.css";

const ImageCropModal = ({
  show,
  imageSrc,
  aspect,
  mimeType,
  fileBaseName,
  outputWidth,
  outputHeight,
  hintKey = "IMAGE_CROP_HINT",
  onHide,
  onCropped,
}) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!show) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setApplying(false);
    }
  }, [show]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleDismiss = () => {
    if (applying) return;
    onHide?.();
  };

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setApplying(true);
    try {
      const outputSize =
        outputWidth && outputHeight
          ? { width: outputWidth, height: outputHeight }
          : undefined;
      const file = await getCroppedImgFile(
        imageSrc,
        croppedAreaPixels,
        fileBaseName,
        mimeType || "image/jpeg",
        outputSize
      );
      onCropped?.(file);
    } catch (_) {
      // Keep modal open so the user can retry.
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleDismiss}
      centered
      className="imageCropModal"
      backdrop={applying ? "static" : true}
      keyboard={!applying}
    >
      <Modal.Header closeButton={!applying}>
        <Modal.Title>{t("IMAGE_CROP_TITLE")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="imageCropModal__body">
        <p className="imageCropModal__hint muted small mb-2">
          {t(hintKey)}
        </p>
        <div className="imageCropModal__cropArea">
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          ) : null}
        </div>
        <label className="imageCropModal__zoomLabel small mt-2">
          {t("IMAGE_CROP_ZOOM")}
          <input
            type="range"
            className="form-range imageCropModal__zoom"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            disabled={!imageSrc || applying}
          />
        </label>
      </Modal.Body>
      <Modal.Footer>
        <CustomButton
          type="button"
          className="black-btn borderButton"
          disabled={applying}
          onClick={handleDismiss}
        >
          {t("CANCEL")}
        </CustomButton>
        <CustomButton
          type="button"
          className="black-btn fillButton"
          loading={applying}
          disabled={!croppedAreaPixels || applying || !imageSrc}
          onClick={handleApply}
        >
          {t("IMAGE_CROP_APPLY")}
        </CustomButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropModal;
